<template>
  <b-container fluid>
    <h3>Purchase Order Lookup (Confidential)</h3>
    <b-form class="mb-4" @submit="getData">
      <b-form-row class="mb-2">
        <b-col>
          <b-row class="mb-2">
            <b-col>
              <b-form-input v-model="form.num" placeholder="PO Number" trim="true"/>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col>
              <b-form-input v-model="form.vendor" placeholder="Vendor Contact" />
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col>
              <b-form-input v-model="form.partnum" placeholder="Part Number" />
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <label>PO Status</label>
          <b-form-select v-model="form.statuses" multiple :options="poStatuses" />
        </b-col>
      </b-form-row>
      <b-form-row class="mb-2">
        <b-col>
          <label>Start Date</label>
          <b-form-input type="date"  v-model="form.start" />
        </b-col>
        <b-col>
          <label>End Date</label>
          <b-form-input type="date" v-model="form.end" />
        </b-col>
      </b-form-row>
      <b-form-row class="mb-2">
        <b-col>
          <b-form-input v-model="form.note" placeholder="PO Note" />
        </b-col>
        <b-col>
          <b-form-input v-model="form.availability_note" placeholder="Availability Note" />
        </b-col>
      </b-form-row>
      <b-form-row class="mb-2">
        <b-col>
          <b-form-input v-model="form.purchasing_note" placeholder="Purchasing Note" />
        </b-col>
        <b-col>
          <b-form-input v-model="form.engineering_note" placeholder="Engineering Note" />
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-btn type="submit">Search</b-btn>
      </b-form-row>
    </b-form>
    <b-row class="mb-2">
      <b-col class="text-right">
        <b-btn @click="toggleAll">
          {{ expanded ? 'Collapse' : 'Expand' }} All
        </b-btn>
      </b-col>
    </b-row>
    <b-row v-if="busy" class="justify-content-md-center">
      <fingerprint-spinner
        :animation-duration="1500"
        :size="300"
        color="#293a93"
        class="my-3"
      />
    </b-row>
    <b-row v-else>
      <b-table :items="items" :fields="mainColumns" class="fixed-table">
        <template slot="dateCreated" slot-scope="data">
          <nice-date :value='String(data.item.dateCreated)' :show-time="false" :is-utc="true" />
        </template>
        <template slot="dateLastModified" slot-scope="data">
          <nice-date :value='String(data.item.dateLastModified)' :show-time="true" :is-utc="true" />
        </template>
        <template slot="dateFirstShip" slot-scope="data">
          <nice-date :value='String(data.item.dateFirstShip)' :show-time="false" :is-utc="true" />
        </template>
        <template slot="details" slot-scope="row">
          <b-button size="sm" @click="row.toggleDetails();">
            {{ row.detailsShowing ? "Hide" : "Show" }} Details
          </b-button>
        </template>
        <template slot="row-details" slot-scope="data">
          <b-row>
            <b-col cols=1>
              <label>Note</label>
            </b-col>
            <b-col>
              {{data.item.note}}
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-table :items="data.item.items" :fields="secondaryColumns" class="table b-table internal-table">
                <template slot="partNum" slot-scope="data">
                  <router-link target="_blank" :to="{name: 'Qty Calculations', query: {partNum: data.item.partNum}}" v-if="data.item.typeid == 10">
                    {{data.item.partNum}}
                  </router-link>
                  <router-link target="_blank" :to="{name: 'Qty Calculations', query: {partNum: data.item.partNum.split('-').slice(1).join('-')}}" v-else-if="data.item.partNum.includes('-')">
                    {{data.item.partNum}}
                  </router-link>
                  <span v-else>
                    {{data.item.partNum}}
                  </span>
                </template>
                <template slot="qtyToFulfill" slot-scope="data">
                  {{Number(data.item.qtyToFulfill)}}
                </template>
                <template slot="qtyFulfilled" slot-scope="data">
                  {{Number(data.item.qtyFulfilled)}}
                </template>
              </b-table>
            </b-col>
          </b-row>
        </template>
      </b-table>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { FingerprintSpinner } from 'epic-spinners';
import NiceDate from '../utility/NiceDate'
const BASE_API = process.env.VUE_API_ROOT;

export default {
  components: {
    NiceDate,
    FingerprintSpinner,
  },
  data() {
    return {
      form: {
        num: '',
        vendor: '',
        statuses: [''],
        note: '',
        start: '',
        end: '',
        partnum: '',
        availability_note: '',
        purchasing_note: '',
        engineering_note: ''
      },
      busy: false,
      expanded: false,
      poStatuses: [],
      items: [],
      mainColumns: [
        { key: 'ponum', label: 'PO Num', sortable: true },
        { key: 'vendorContact', sortable: true },
        { key: 'status', sortable: true },
        { key: 'dateCreated', sortable: true },
        { key: 'dateLastModified', sortable: true },
        { key: 'dateFirstShip', sortable: true },
        { key: 'details' },
      ],
      secondaryColumns: [
        { key: 'partNum' },
        { key: 'description' },
        { key: 'qtyToFulfill' },
        { key: 'qtyFulfilled' },
        { key: 'availabilityNote' },
        { key: 'purchasingNote' },
        { key: 'engineeringNote' }
      ],
    };
  },
  mounted() {
    axios.get(`${BASE_API}/fishbowl/purchase-orders/statuses`).then(response => {
      this.poStatuses = response.data.map(item => ({ value: item.name, text: item.name }));
      this.poStatuses.splice(0, 0, { value: '', text: 'Any' });
    }).catch(error => {
      console.error(error);
    })

    if (Object.keys(this.$route.query).length > 0) {
      Object.keys(this.$route.query).forEach((key) => {
        if (this.form.hasOwnProperty(key)) {
          this.form[key] = this.$route.query[key];
        }
      });

      this.getData();
    }
  },
  methods: {
    getData(event) {
      if (event) event.preventDefault();

      this.items.splice(0);
      this.busy = true;

      axios({
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/fishbowl/purchase-orders`,
        data: this.form
      }).then(response => {
        this.items = response.data;
        if (this.items.length === 1) {
          this.toggleAll();
        }
      }).catch(error => {
        console.error(error);
      }).finally(() => {
        this.busy = false;
      });
    },
    toggleAll(event) {
      if (event) event.preventDefault();

      this.expanded = !this.expanded;
      this.items.forEach(x => {
        x._showDetails = this.expanded;
      });
    },
  },
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/purchasing/PoReport.vue